import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { Box, Button, Card } from '@mui/material';
import { GridCallbackDetails, GridColDef, GridRow, GridRowId, GridRowModel, GridRowSelectionModel } from '@mui/x-data-grid';
import LoadingButton from '@mui/lab/LoadingButton';
import { TablePaginated } from '../Manage/components';
import { getFragmentData, gql } from '../../__generated__';
import QuickSearchToolbar, { QuickSearchToolbarProps } from '../components/SearchBar/SearchBar';
import useStudentPersonsAssignment from './hooks/useStudentPersonsAssignment';
import { useSelectedStudent } from './hooks';
import { useManageModal } from '../Manage/components/ManageModal';
import { _PersonFragment } from '../../__generated__/graphql';
import { FetchMoreCallbackArgs } from '../Manage/components/TablePaginated/TablePaginated';

export const STUDENT_PERSONS_LINK_QUERY = gql(/* GraphQL */`
    query studentPersonsLink($locationId: UUID,$studentId:UUID, $query:String,$first:Int, $after:String ) {
        location(id:$locationId) {
          persons (status: ACTIVE,query:$query, first:$first, after:$after) {
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
            totalCount
            edges{
              cursor
              node {
                ..._person
              }
            }
          }
        }
    }
`);

const PERSON_FRAGMENT = gql(/* GraphQL */`
    fragment _person on Person {
        id
        firstName
        middleName
        lastName
        jobTitle
        email
        startDate
        endDate
        expired
        role {
            name
            normalizedName
        }
        students(ids: [ $studentId ], status: ACTIVE) {
                    edges {
                        node {
                            id
                            references
                        }
                    }
                    totalCount
                }
    }
`);


type RouteParams = {
    sid?: string
}

type ActionOnCallback = () => void;


const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}

const mapEdgesToTable = (data: any) => {
    // @ts-ignore because CI/CD finds fault with this line
    const fragment = getFragmentData(PERSON_FRAGMENT, data.node);
    return {
        ...data.node,
        id: fragment.id,
        firstName: fragment.firstName,
        lastName: fragment.lastName,
        name: `${fragment.lastName} ${fragment.firstName}`,
        userType: fragment.role?.name,
        email: fragment.email,
        jobTitle: fragment.jobTitle,
        role: fragment.role,
        students: fragment.students
    } as const
}
function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {

    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    const updated = [
        ...c.filter((x: any) => !removed.includes(x)),
        ...added
    ]
    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

const TABLE_COLUMNS: GridColDef[] = [
    {
        field: "firstName",
        headerName: "First Name",
        flex: 1,
        sortable: true
    },
    {
        field: "lastName",
        headerName: "Last Name",
        flex: 1,
        sortable: true
    },
    {
        field: "userType",
        headerName: "User Type",
        flex: 1,
        sortable: true
    },
    {
        field: "email",
        headerName: "Email",
        flex: 1,
        sortable: true
    },
    {
        field: "jobTitle",
        headerName: "Job Title",
        flex: 1,
        sortable: true
    }
];

interface PersonTableLinkContainerProps {
    children: React.ReactNode
}

interface PersonTableLinkTableToolbarProps extends QuickSearchToolbarProps { }

interface PersonTableLinkProps {
    onBack: ActionOnCallback
}

interface PesonTableLinkFooterProps {
    saving: boolean,
    disabled: boolean,
    onBack: ActionOnCallback
    onSave: ActionOnCallback
}

const PersonTableLink = ({
    onBack
}: PersonTableLinkProps) => {

    const routeParams = useParams<RouteParams>();

    const { loading: loadingStudent, student, setSelectedStudent } = useSelectedStudent(routeParams.sid);

    const { data, loading, fetchMore } = useQuery(STUDENT_PERSONS_LINK_QUERY, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        skip: !student?.id || !student?.locationId,
        variables: {
            studentId: student?.id,
            locationId: student?.locationId,
            query: "",
            first: 10,
        }
    });

    const [tableState, setTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[],
        selectionModel: GridRowSelectionModel,
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[],
        },
    }>({
        columns: [],
        rows: [],
        selectionModel: { type: 'include', ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [], // Contains student IDs for selections on table/page
        },
    });

    const [saving, setSaving] = useState<boolean>(false);
    const [pageLoading, setPageLoading] = useState<boolean>(false);
    const { save } = useStudentPersonsAssignment(routeParams.sid ?? '');

    const { openModal } = useManageModal();


    useEffect(() => {
        if (data) {
            setTableState((prevState) => {
                const columns = [...TABLE_COLUMNS];
                const rows = data?.location?.persons?.edges?.map(mapEdgesToTable).filter(x => x.userType !== "Support Admin") ?? []

                const selectionModel: GridRowSelectionModel = { type: 'include', ids: new Set<GridRowId>() }
                for (let i = 0; i < (data?.location?.persons?.edges?.length ?? 0); i++) {
                    const person = data?.location?.persons?.edges ? data?.location?.persons?.edges[i].node : null;
                    if (!person) continue;

                    const personFragment = getFragmentData(PERSON_FRAGMENT, person);

                    for (let j = 0; j < (personFragment.students?.edges?.length ?? 0); j++) {
                        const studentEdge = personFragment.students?.edges?.[j]
                        if (!studentEdge) continue;

                        const isAssigned = studentEdge.node.id === routeParams.sid;
                        if (isAssigned) {
                            if (tableState.selections.unassign.includes(studentEdge.node.id)) continue;

                            selectionModel.ids.add(personFragment.id);
                        }
                    }
                }
                setPageLoading(false)

                const _ids = Array.from(selectionModel.ids);

                return {
                    ...prevState,
                    columns,
                    rows,
                    // The filter clause is required because due to the multi-role user function of the API (currently unsupported)
                    // a user that has roles other than "ADMIN" is returned. User roles are resolved in a separate resolver. This
                    // separate resolver ranks the roles assigned to the person/user. If the user has multiple roles, including
                    // admin, then the admin role is returned. This is because the ADMIN role is ranked the highest. 
                    //
                    // In reality a user having multiple roles shouldn't happen, but the system supports it, the filter
                    // protects against it.
                    selectionModel,
                    selections: {
                        ...prevState.selections,
                        initial: _ids,
                        pageAssign: _ids,
                        prevAssign: unique(prevState.selections.prevAssign.concat(Array.from(selectionModel.ids))),
                        allSelected: Array.from(selectionModel.ids).concat(prevState.selections.nextAssign)
                    }
                }
            });
        }
    }, [loading, data]);
    useEffect(() => { console.log("the rows: ", tableState.rows) }, [tableState.rows])
    const handleSelectionModelChange = (values: GridRowSelectionModel, row: GridCallbackDetails) => {
        // Sometimes when the page is reloaded, the Table/DataGrid component triggers automatically 
        // without any user intervention. This callback function should only trigger
        // if {@see GridControlledStateReasonLookup} actions happen. However,
        // the reason is undefined (it shouldn't be). So in this case
        // we ignore this false called to preserve our selection model
        // if (!row?.reason) return;

        const selectedIds = Array.from(values.ids)
        const prevSelectedIds = Array.from(tableState.selectionModel.ids);
        const assign = selectedIds.filter(x => !prevSelectedIds.includes(x));
        const unassign = prevSelectedIds.filter(x => !selectedIds.includes(x));
        if ((selectedIds.length == 0) && unassign.length > 0) {
            openModal({ type: "NOT_ENOUGH_REFERENCES", cancelButtonLabel: "Close" })
            return;
        }
        const uniqAssign = assign.filter(x => !tableState.selections.prevAssign.includes(x));
        setTableState(prevState => {

            const nextAssign = unique(deltaSelections(uniqAssign, unassign, prevState.selections.nextAssign));
            const nextUnassign = deltaSelections(unassign, assign, prevState.selections.unassign);
            const selectionModel = {
                ...prevState,
                selectionModel: values,
                selections: {
                    ...prevState.selections,
                    nextAssign,
                    unassign: nextUnassign,
                    allSelected: unique(selectedIds.concat(nextAssign).filter(x => x !== unassign[0]))
                }
            }

            return selectionModel;
        })
    }

    const handleSave = async () => {

        // In theory, this save event shouldn't fire because the save button should be
        // disabled. However, for completeness sake, we exit the event early if 
        // there's nothing to save.
        if (tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)
            return;

        setSaving(true);

        // When not in development mode this debug code is removed (tree shaken).
        if (process.env.NODE_ENV === 'development') {
            console.group('handleSave');
            // console.log('Assign', tableState.selections.nextAssign);
            // console.log('Unassign', tableState.selections.unassign);
            console.groupEnd();
        }

        const references = await save(tableState.selections.nextAssign, tableState.selections.unassign);
        setSelectedStudent(prevStudent => ({
            __typename: 'Student',
            ...prevStudent,
            references: references
        }));

        // console.log('Attempt to save assignments');

        setTableState(prevState => ({
            ...prevState,
            selections: {
                ...prevState.selections,
                pageAssign: [],
                prevAssign: [],
                nextAssign: [],
                unassign: []
            }
        }))

        setSaving(false);

        openModal({
            type: 'SAVED_ASSIGNMENTS',
            onConfirm: undefined,
            cancelButtonLabel: 'Close'
        });
    }

    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        // if (!student?.id || !student?.locationId) return;
        setPageLoading(true)
        await fetchMore({
            variables: {
                studentId: student?.id,
                locationId: student?.locationId,
                query: variables.query ?? "",
                after: variables.after ?? null,
                first: variables.first ?? 10
            }
        }).then((res) => {
            setPageLoading(false)
            // TODO: UNDERSTAND WHY THIS IS NOT WORKING AS INTENDED, FOR NOW THIS IS A WORK AROUND 08-22-2024
            const rows = res?.data?.location?.persons?.edges?.map(mapEdgesToTable).filter(x => x.userType !== "Support Admin") ?? []
            if (rows && rows.length > 0) {
                setTableState(prevState => ({
                    ...prevState,
                    rows
                }))

            }
        }).catch(err => {
            console.log('the is an error: ', err)
        })
    }
    return (
        <PersonTableLink.Container>
            <TablePaginated
                loading={loading || pageLoading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <PersonTableLink.TableToolbar
                            title={!loading
                                ? `Link Users for ${student?.firstName} ${student?.lastName}`
                                : 'Loading...'
                            }
                            value={searchQuery}
                            onChange={onSearch}
                            onClickBack={onBack}
                            clearSearch={onSearchClear} />
                    )
                }}
                pageInfo={data?.location?.persons?.pageInfo}
                totalRows={data?.location?.persons?.totalCount ?? 0}
                columns={tableState.columns}
                rows={pageLoading ? [] : tableState.rows}
                pageSize={10}
                slots={{
                    row: (props: any) => {

                        const person = props.row as _PersonFragment;
                        const selected = tableState.selections.initial.includes(props.row.id);
                        const found = person.students?.edges ? person.students.edges[0] : null;

                        /*
                         * `props.selected` is not true when the row is disabled using `isRowSelectable` 🤦‍♂️
                         * so to achieve the desired result, we look at the intial selection modal i.e., 
                         * all identified person/student connections found at time of loeading the
                         * component
                         */
                        const disabled = ((found?.node?.references ?? 0) <= 1 && found && selected);

                        const handleDisabledClick = () => {
                            openModal({ type: 'NOT_ENOUGH_REFERENCES' })
                        };

                        return (
                            <div
                                style={{
                                    background: disabled ? '#F5F5F5' : 'initial',
                                    color: disabled ? '#777' : 'initial',
                                    cursor: disabled ? 'not-allowed' : 'default'
                                }}
                                onClick={disabled ? handleDisabledClick : undefined}>
                                <GridRow {...props} />
                            </div>
                        )
                    }
                }}
                isRowSelectable={(params) => {
                    const person = params.row as _PersonFragment;
                    const selected = tableState.selections.initial.includes(params.row.id);
                    const found = person.students?.edges ? person.students.edges[0] : null;

                    const disabled = !((found?.node?.references ?? 0) <= 1 && found && selected);
                    return disabled;
                }}

                selectionProps={{
                    rowSelectionModel: { type: 'include', ids: new Set(tableState.selections.allSelected) },
                    onRowSelectionModelChange: handleSelectionModelChange
                }}
                disableBoxShadow={true}
                checkboxSelection={true}
                onFetchMore={handleFetchMore}
            />
            <PersonTableLink.Footer
                disabled={(tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)}
                saving={saving}
                onBack={onBack}
                onSave={handleSave} />
        </PersonTableLink.Container>
    )
}

PersonTableLink.TableToolbar = (props: PersonTableLinkTableToolbarProps) => {
    return (
        <Box component="div">
            <QuickSearchToolbar {...props} />
        </Box>
    );
}

PersonTableLink.Container = ({ children }: PersonTableLinkContainerProps): React.ReactElement => {
    return (
        <Card
            elevation={4}
            style={{
                width: "100%",
                height: "auto",
            }}
        >
            <>{children}</>
        </Card>
    )
}

PersonTableLink.Footer = ({ disabled, saving, onSave, onBack }: PesonTableLinkFooterProps): React.ReactElement => {
    return (
        <Box
            component="div"
            sx={{ padding: "16px", display: "flex", justifyContent: "flex-end" }}
        >
            <Button
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    marginRight: "16px",
                    backgroundColor: "#C4C4C4",
                    "&:hover": {
                        color: "#FFF",
                        backgroundColor: "red.main",
                    },
                }}
                onClick={onBack}
            >
                Cancel
            </Button>
            <LoadingButton
                loading={saving}
                disabled={disabled}
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                        backgroundColor: "primary.light",
                    },
                }}
                onClick={onSave}
            >
                Save
            </LoadingButton>
        </Box>
    )
}

export default PersonTableLink;